import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '@/lib/cart';
import { toast } from 'sonner';

const ArtworkDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t, lang } = useI18n();
  const { addItem } = useCart();
  const [selectedImage, setSelectedImage] = useState(0);

  const { data: artwork, isLoading } = useQuery({
    queryKey: ['artwork', id],
    queryFn: async () => {
      const { data } = await supabase.from('artworks').select('*').eq('id', id!).single();
      return data;
    },
    enabled: !!id,
  });

  const { data: extraImages = [] } = useQuery({
    queryKey: ['artwork-images', id],
    queryFn: async () => {
      const { data } = await supabase.from('artwork_images').select('*').eq('artwork_id', id!).order('display_order');
      return data || [];
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-muted-foreground">Artwork not found</p>
      </div>
    );
  }

  const title = lang === 'ar' && artwork.title_ar ? artwork.title_ar : artwork.title;
  const description = lang === 'ar' && artwork.description_ar ? artwork.description_ar : artwork.description;

  // All images: main + extras
  const allImages = [
    artwork.image_url,
    ...extraImages.map(img => img.image_url),
  ].filter(Boolean) as string[];

  return (
    <div className="min-h-screen pt-16">
      <div className="container mx-auto px-4 py-8 md:py-12">
        <Link to="/gallery" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft className={`h-4 w-4 ${lang === 'ar' ? 'rotate-180' : ''}`} />
          {t('backToGallery')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Images */}
          <div>
            <div className="relative overflow-hidden rounded-xl glass-panel border-0">
              <img
                src={allImages[selectedImage] || '/placeholder.svg'}
                alt={title}
                className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              />
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === i ? 'border-primary shadow-glow' : 'border-border/30 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center glass-panel p-6 md:p-10 rounded-2xl border-0">
            <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium capitalize mb-3 w-fit">
              {artwork.category}
            </span>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">{title}</h1>
            {description && (
              <p className="text-muted-foreground leading-relaxed mb-6 text-sm md:text-base">{description}</p>
            )}
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-2xl md:text-3xl font-bold text-primary">{t('currency')}{artwork.price}</span>
            </div>

            <Button
              size="lg"
              onClick={() => {
                addItem({
                  id: artwork.id,
                  title: artwork.title,
                  title_ar: artwork.title_ar,
                  price: artwork.price,
                  image_url: artwork.image_url,
                });
                toast.success(lang === 'ar' ? 'تمت الإضافة للسلة' : 'Added to cart');
              }}
              className="gradient-accent text-primary-foreground shadow-glow hover:opacity-90 transition-opacity w-full sm:w-auto"
            >
              <ShoppingBag className={`h-4 w-4 ${lang === 'ar' ? 'ml-2' : 'mr-2'}`} />
              {t('addToCart')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkDetails;
